import { inject } from '@angular/core';
import { Router, CanActivateFn, CanActivateChildFn } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

/**
 * Keeps Support Staff on the Conversations page.
 *
 * This is the `support` role — people an organisation invites to answer
 * conversations — not a Solenex support session (supportModeGuard, the
 * `impersonating` claim). Support Staff are not Echo admins: Conversations is
 * the whole app for them, so any other route in the signed-in layout redirects
 * there. That includes the "" -> dashboard default, so it is also where they
 * land after sign-in or accepting an invitation.
 *
 * Like supportModeGuard this is not the security boundary — Firestore rules
 * and the callables are. It stops a bookmark or a stray link from opening a
 * page that would only fail for them.
 *
 * Reads the claim directly, like supportModeGuard, so it does not wait on
 * PermissionsService or a Firestore read on every child navigation.
 *
 * Usage: canActivateChild: [conversationsOnlyGuard] on a layout route, or
 * canActivate: [conversationsOnlyGuard] on a single route.
 */
export const conversationsOnlyGuard: CanActivateFn & CanActivateChildFn = async (_route, state) => {
    const auth = inject(Auth);
    const router = inject(Router);

    try {
        const user = await firstValueFrom(authState(auth).pipe(take(1)));
        if (!user) return true; // AuthGuard owns this case.

        const token = await user.getIdTokenResult();
        if (token.claims['role'] !== 'support') return true;

        const path = state.url.split(/[?#]/)[0];
        if (path === '/conversations' || path.startsWith('/conversations/')) {
            return true;
        }

        return router.createUrlTree(['/conversations']);
    } catch (error) {
        // Fail open, like the other customer-facing guards: the pages behind
        // this are guarded on their own, and the server is the real gate.
        console.error('Conversations-only guard error:', error);
        return true;
    }
};
